import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ListChecks, ExternalLink, Loader2 } from "lucide-react";
import PageHeader from "@/layout/PageHeader";
import { API, authHeaders } from "@/lib/api";


async function fetchSurvey(id) {
  const r = await fetch(`${API}/weekly-surveys/${id}`, { headers: authHeaders() });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}


function OptionCard({ letter, text }) {
  return (
    <div className="rounded-lg border border-hm-border bg-hm-bg-1 p-4 flex items-start gap-3"
         data-testid={`survey-option-${letter}`}>
      <div className="shrink-0 h-8 w-8 rounded-md bg-hm-accent/15 text-hm-accent font-mono font-bold flex items-center justify-center text-sm">
        {letter}
      </div>
      <div className="text-sm text-hm-fg flex-1 leading-snug pt-1">{text}</div>
    </div>
  );
}


export default function WeeklySurveyDetail() {
  const { id } = useParams();
  const { data: s, isPending, isError, error } = useQuery({
    queryKey: ["weekly-survey", id],
    queryFn: () => fetchSurvey(id),
    staleTime: 60_000,
  });

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-hm-fg-subtle" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="hm-surface p-6" data-testid="survey-detail-error">
        <p className="text-sm text-hm-fg">Failed to load: {error?.message}</p>
      </div>
    );
  }

  const options = [s.option_a, s.option_b, s.option_c, s.option_d];
  const letters = ["A", "B", "C", "D"];
  const sources = s.anchor_source_urls || [];
  const gen = s.generation_metadata || {};
  const genStats = gen.gen_stats || {};

  return (
    <div data-testid="survey-detail">
      <PageHeader
        title="Weekly Survey"
        subtitle={`Survey date: ${s.survey_date}`}
        actions={
          <Link to="/weekly-surveys"
                className="inline-flex items-center gap-1.5 text-xs text-hm-fg-muted hover:text-hm-fg">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </Link>
        }
      />
      <div className="grid lg:grid-cols-[1.6fr_1fr] gap-6">
        <div className="hm-surface p-6" data-testid="survey-content">
          <div className="flex items-center gap-2 mb-2">
            <ListChecks className="h-4 w-4 text-hm-accent" />
            <span className="mono text-[11px] uppercase tracking-widest text-hm-fg-subtle">
              Topic: {s.anchor_topic}
            </span>
          </div>
          <h2 className="text-xl font-semibold text-hm-fg leading-snug mb-6"
              data-testid="survey-question">
            {s.question}
          </h2>
          <div className="grid gap-3">
            {options.map((o, i) => (
              <OptionCard key={i} letter={letters[i]} text={o} />
            ))}
          </div>
          {sources.length > 0 && (
            <div className="mt-6 pt-6 border-t border-hm-border">
              <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-2">
                Anchor sources
              </div>
              <ul className="space-y-1">
                {sources.map((u, i) => (
                  <li key={i}>
                    <a href={u} target="_blank" rel="noreferrer"
                       className="text-xs text-hm-accent hover:underline inline-flex items-center gap-1 break-all">
                      <ExternalLink className="h-3 w-3 shrink-0" />
                      {u}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="hm-surface p-6" data-testid="survey-metadata">
          <div className="mono text-[10px] uppercase tracking-widest text-hm-fg-subtle mb-4">
            Generation metadata
          </div>
          <dl className="space-y-3 text-xs">
            <div className="flex justify-between gap-2"><dt className="text-hm-fg-muted">Status</dt><dd className="text-hm-fg font-medium">{s.status}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-hm-fg-muted">Model</dt><dd className="text-hm-fg mono">{gen.model || "—"}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-hm-fg-muted">Duration</dt><dd className="text-hm-fg mono">{gen.duration_s ? `${gen.duration_s}s` : "—"}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-hm-fg-muted">LLM attempts</dt><dd className="text-hm-fg mono">{genStats.attempts ?? "—"}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-hm-fg-muted">Retries</dt><dd className="text-hm-fg mono">{genStats.retries ?? "—"}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-hm-fg-muted">Dedup hits</dt><dd className="text-hm-fg mono">{genStats.dedup_hits ?? 0}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-hm-fg-muted">Articles pooled</dt><dd className="text-hm-fg mono">{(gen.collect_stats || {}).total_articles ?? "—"}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-hm-fg-muted">Sent at</dt><dd className="text-hm-fg mono">{s.sent_at || "—"}</dd></div>
            <div className="flex justify-between gap-2"><dt className="text-hm-fg-muted">Resend id</dt><dd className="text-hm-fg mono text-[10px] break-all">{s.resend_message_id || "—"}</dd></div>
          </dl>
          <div className="mt-6 pt-6 border-t border-hm-border">
            <a
              href={`${API}/weekly-surveys/${s.id}/preview-email`}
              target="_blank"
              rel="noreferrer"
              data-testid="survey-preview-email-link"
              className="inline-flex items-center gap-1.5 text-xs text-hm-accent hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open email preview
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
